import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getMe, updateMe, getMyItems, deleteItem, startSupportChat } from '../api/api';
import { useLanguage } from '../contexts/LanguageContext';
import { Loader2, Mail, LogOut, Star, Repeat, Edit, Plus, MessageCircle, Share2, Eye, EyeOff, X, User as UserIcon, Phone, MapPin, ShieldCheck, Check, Flame, ThumbsUp, ThumbsDown, Award, Zap } from 'lucide-react';
import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { useForm } from 'react-hook-form';
import ItemCard from '../components/items/ItemCard';
import { Link, useNavigate } from 'react-router-dom';
import ImageWithFallback from '../components/common/ImageWithFallback';
import PageInfo from '../components/common/PageInfo';
import Breadcrumbs from '../components/common/Breadcrumbs';
import SEO from '../components/common/SEO';
import { motion, AnimatePresence } from 'framer-motion';

function EditProfileModal({ isOpen, onClose, user }) {
  const { t } = useLanguage();
  const queryClient = useQueryClient();
  const { register, handleSubmit, reset } = useForm({
      defaultValues: {
          full_name: user?.full_name,
          bio: user?.bio,
          email: user?.email,
          phone: user?.phone,
          location: user?.location,
      }
  });
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    if (isOpen && user) {
      reset({
          full_name: user.full_name,
          bio: user.bio,
          email: user.email,
          phone: user.phone,
          location: user.location,
          password: ''
      });
    }
  }, [isOpen, user, reset]);

  const updateMutation = useMutation({
    mutationFn: (data) => updateMe(data),
    onSuccess: (data) => {
      toast.success(t('statusUpdated', 'Profile updated'));
      const currentUser = JSON.parse(localStorage.getItem('swapx_user') || '{}');
      localStorage.setItem('swapx_user', JSON.stringify({ ...currentUser, ...data }));
      queryClient.invalidateQueries(['user', 'me']);
      onClose();
    },
    onError: (err) => toast.error(t(err.message) || err.message)
  });

  const onSubmit = (data) => {
      // Remove empty password if not changing
      if (!data.password) delete data.password;
      updateMutation.mutate(data);
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[1000] flex items-center justify-center p-4 sm:p-6">
          <motion.div 
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={onClose} className="absolute inset-0 bg-black/60 backdrop-blur-sm" 
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            className="relative bg-background w-full max-w-lg rounded-[2.5rem] shadow-2xl overflow-hidden border border-border flex flex-col max-h-[90vh]"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6 border-b border-border flex justify-between items-center bg-muted/20">
                <h2 className="text-xl font-black uppercase tracking-tighter flex items-center gap-2">
                    <Edit size={20} className="text-primary" />
                    {t('editProfile')}
                </h2>
                <button onClick={onClose} className="p-2 hover:bg-muted rounded-full transition-colors"><X size={20}/></button>
            </div>

            <div className="p-8 overflow-y-auto">
                <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                    <div className="space-y-4">
                        <div className="space-y-1">
                            <label className="text-[10px] font-black uppercase tracking-widest text-muted-foreground ml-1">{t('fullName')}</label>
                            <div className="relative">
                                <UserIcon className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground w-4 h-4" />
                                <input {...register('full_name')} className="w-full bg-muted/50 border-none rounded-2xl py-3 pl-11 pr-4 focus:ring-2 focus:ring-primary/20 font-bold text-sm" />
                            </div>
                        </div>

                        <div className="space-y-1">
                            <label className="text-[10px] font-black uppercase tracking-widest text-muted-foreground ml-1">{t('email')}</label>
                            <div className="relative">
                                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground w-4 h-4" />
                                <input {...register('email')} className="w-full bg-muted/50 border-none rounded-2xl py-3 pl-11 pr-4 focus:ring-2 focus:ring-primary/20 font-bold text-sm" />
                            </div>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-1">
                                <label className="text-[10px] font-black uppercase tracking-widest text-muted-foreground ml-1">{t('phoneNumber')}</label>
                                <div className="relative">
                                    <Phone className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground w-4 h-4" />
                                    <input {...register('phone')} className="w-full bg-muted/50 border-none rounded-2xl py-3 pl-11 pr-4 focus:ring-2 focus:ring-primary/20 font-bold text-sm" />
                                </div>
                            </div>
                            <div className="space-y-1">
                                <label className="text-[10px] font-black uppercase tracking-widest text-muted-foreground ml-1">{t('location')}</label>
                                <div className="relative">
                                    <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground w-4 h-4" />
                                    <input {...register('location')} className="w-full bg-muted/50 border-none rounded-2xl py-3 pl-11 pr-4 focus:ring-2 focus:ring-primary/20 font-bold text-sm" />
                                </div>
                            </div>
                        </div>

                        <div className="space-y-1">
                            <label className="text-[10px] font-black uppercase tracking-widest text-muted-foreground ml-1">Bio</label>
                            <textarea {...register('bio')} rows="3" className="w-full bg-muted/50 border-none rounded-2xl p-4 focus:ring-2 focus:ring-primary/20 font-bold text-sm resize-none" />
                        </div>

                        <div className="space-y-1 pt-4 border-t border-border">
                            <label className="text-[10px] font-black uppercase tracking-widest text-red-500 ml-1">{t('password')}</label>
                            <div className="relative">
                                <ShieldCheck className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground w-4 h-4" />
                                <input 
                                    type={showPassword ? "text" : "password"}
                                    {...register('password')} 
                                    className="w-full bg-muted/50 border-none rounded-2xl py-3 pl-11 pr-12 focus:ring-2 focus:ring-primary/20 font-bold text-sm"
                                    placeholder="••••••••"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-primary transition-colors"
                                >
                                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                                </button>
                            </div>
                            <p className="text-[9px] text-muted-foreground mt-1 ml-1">Leave blank to keep current password</p>
                        </div>
                    </div>

                    <button
                        type="submit"
                        disabled={updateMutation.isLoading}
                        className="w-full bg-primary text-primary-content py-4 rounded-2xl font-black uppercase tracking-widest text-sm shadow-xl shadow-primary/20 hover:scale-[1.02] active:scale-[0.98] transition-all flex items-center justify-center gap-2"
                    >
                        {updateMutation.isLoading ? <Loader2 className="animate-spin" /> : <><Check size={18} /> {t('saveChanges')}</>}
                    </button>
                </form>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}

export default function Profile() {
  const { t } = useLanguage();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  
  const { data: user, isLoading, error } = useQuery({
    queryKey: ['user', 'me'],
    queryFn: getMe,
    retry: false,
  });

  const { data: myItemsData, isLoading: isLoadingItems } = useQuery({
    queryKey: ['items', 'my'],
    queryFn: getMyItems,
  });
  const myItems = myItemsData?.items || myItemsData || [];

  const deleteMutation = useMutation({
    mutationFn: deleteItem,
    onSuccess: () => {
        toast.success('Item removed');
        queryClient.invalidateQueries(['items', 'my']);
    },
    onError: (err) => toast.error(err.message)
  });

  const handleLogout = () => {
    localStorage.removeItem('swapx_user');
    window.location.href = '/';
  };

  const handleShareProfile = () => {
    const profileUrl = `${window.location.origin}/profile/${user._id}`;
    navigator.clipboard.writeText(profileUrl);
    toast.success(t('profileLinkCopied', 'Profile link copied to clipboard!'));
  };

  const handleEditItem = (id) => {
      navigate(`/edit-item/${id}`);
  };

  const supportMutation = useMutation({
      mutationFn: startSupportChat,
      onSuccess: () => {
          navigate('/messages');
      },
      onError: (err) => toast.error(err.message)
  });

  if (isLoading) return <div className="flex justify-center py-20"><Loader2 className="w-12 h-12 animate-spin text-primary"/></div>
  if (error || !user) return <div className="text-center py-20"><p className="text-red-500 font-bold mb-4">Please log in to view your profile.</p><Link to="/login" className="bg-primary text-white px-8 py-3 rounded-xl font-black uppercase tracking-widest text-xs">Login</Link></div>

  return (
    <div className="container mx-auto p-4 max-w-5xl space-y-8 pb-20">
      <SEO title={t('profile')} description={t('profileInfo.description')} />
      <Breadcrumbs items={[{ label: t('navProfile') }]} />
      {/* Profile Header Section */}
      <div className="relative group">
        <div className="absolute -inset-1 bg-gradient-to-r from-primary via-purple-600 to-secondary rounded-[3rem] blur opacity-25 group-hover:opacity-40 transition duration-1000"></div>
        <div className="relative bg-card rounded-[3rem] shadow-2xl p-6 sm:p-8 md:p-12 border border-white/10 overflow-hidden">
          {/* How to Use / PageInfo Badge - Absolute Top-Right Corner for perfect mobile & desktop alignment */}
          <div className="absolute top-5 right-5 rtl:right-auto rtl:left-5 z-20">
            <PageInfo infoKey="profileInfo" />
          </div>

          {/* Background Decoration */}
          <div className="absolute top-0 right-0 w-64 h-64 bg-primary/5 rounded-full -mr-32 -mt-32 blur-3xl"></div>
          <div className="absolute bottom-0 left-0 w-48 h-48 bg-secondary/5 rounded-full -ml-24 -mb-24 blur-3xl"></div>

          <div className="relative flex flex-col items-center md:flex-row md:items-start md:gap-10">
            <div className="relative shrink-0">
              <div className="absolute -inset-2 bg-gradient-to-tr from-primary to-purple-500 rounded-full blur-sm opacity-50 animate-pulse"></div>
              <div className="w-32 h-32 md:w-40 md:h-40 relative">
                <ImageWithFallback 
                  src={user.avatar || `https://avatar.vercel.sh/${user.email}.svg`} 
                  alt="avatar" 
                  className="relative w-full h-full rounded-full object-cover border-4 border-background shadow-2xl" 
                />
              </div>
              <button 
                onClick={() => setIsEditModalOpen(true)}
                className="absolute bottom-1 right-1 bg-primary text-primary-content p-3 rounded-full shadow-lg hover:scale-110 transition-transform border-4 border-background active:scale-95"
                title="Edit Profile"
              >
                <Edit className="w-4 h-4" />
              </button>
            </div>

            <div className="mt-6 md:mt-0 text-center md:text-left flex-grow space-y-4 pr-8 rtl:pr-0 rtl:pl-8">
              <div>
                <div className="flex flex-wrap items-center justify-center md:justify-start gap-2 mb-1">
                  <h1 className="text-3xl sm:text-4xl md:text-5xl font-black tracking-tighter uppercase">{user.full_name}</h1>
                  <span className="px-3 py-1 bg-primary/10 text-primary border border-primary/20 text-[10px] font-black uppercase tracking-widest rounded-full">
                    {user.role === 'admin' ? '🛡️ Super Admin' : user.role === 'moderator' ? '⚡ Moderator' : '🌱 Verified Trader'}
                  </span>
                </div>
                
                <div className="flex flex-wrap items-center justify-center md:justify-start gap-3 text-muted-foreground font-bold text-xs uppercase tracking-widest">
                  <div className="flex items-center gap-1.5 bg-muted/30 px-3 py-1.5 rounded-xl border border-border/50">
                    <Mail size={14} className="text-primary" />
                    <span>{user.email}</span>
                  </div>
                  {user.phone && (
                    <div className="flex items-center gap-1.5 bg-muted/30 px-3 py-1.5 rounded-xl border border-border/50">
                      <Phone size={14} className="text-emerald-500" />
                      <span>{user.phone}</span>
                    </div>
                  )}
                  {user.location && (
                    <div className="flex items-center gap-1.5 bg-muted/30 px-3 py-1.5 rounded-xl border border-border/50">
                      <MapPin size={14} className="text-rose-500" />
                      <span>{user.location}</span>
                    </div>
                  )}
                </div>
              </div>

              {user.bio ? (
                <p className="text-sm sm:text-base text-muted-foreground leading-relaxed max-w-2xl italic">"{user.bio}"</p>
              ) : (
                <button onClick={() => setIsEditModalOpen(true)} className="text-primary text-xs font-bold hover:underline flex items-center gap-1.5 mx-auto md:mx-0">
                  <Plus size={14} /> Add bio to your profile
                </button>
              )}

              {/* Action Buttons */}
              <div className="flex flex-wrap gap-3 justify-center md:justify-start pt-2">
                <button 
                  type="button" 
                  onClick={() => supportMutation.mutate()} 
                  disabled={supportMutation.isLoading}
                  className="bg-blue-500 text-white shadow-lg shadow-blue-500/20 px-6 py-3 rounded-2xl text-xs font-black uppercase tracking-widest hover:opacity-90 transition-all flex items-center gap-2 active:scale-95"
                >
                  {supportMutation.isLoading ? <Loader2 className="animate-spin h-4 w-4" /> : <><MessageCircle size={16}/> {t('contactSupport')}</>}
                </button>

                <button 
                  type="button"
                  onClick={handleShareProfile} 
                  className="bg-muted text-foreground border border-border px-6 py-3 rounded-2xl text-xs font-black uppercase tracking-widest hover:bg-muted/80 transition-all active:scale-95 flex items-center gap-2"
                >
                  <Share2 size={16}/>
                  {t('shareProfile', 'Share Profile')}
                </button>
                
                <button 
                  type="button"
                  onClick={handleLogout} 
                  className="px-5 py-3 rounded-2xl text-xs font-black uppercase tracking-widest text-red-500 bg-red-500/10 border border-red-500/20 hover:bg-red-500 hover:text-white transition-all active:scale-95 flex items-center gap-2"
                >
                  <LogOut size={16}/>
                  {t('logout')}
                </button>
              </div>
            </div>
          </div>

          {/* Stats Bar */}
          <div className="mt-8 pt-8 border-t border-white/10 grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center group/stat p-3 bg-secondary/30 rounded-2xl border border-white/5">
              <div className="inline-flex p-2.5 bg-purple-500/10 rounded-xl text-purple-500 mb-2">
                <Repeat className="w-5 h-5" />
              </div>
              <h4 className="font-black text-2xl text-foreground block truncate">{user.total_trades || 0}</h4>
              <p className="text-[10px] font-black uppercase tracking-wider text-muted-foreground mt-1 truncate">{t('totalTrades', 'Total Trades')}</p>
            </div>

            <div className="text-center group/stat p-3 bg-secondary/30 rounded-2xl border border-white/5">
              <div className="inline-flex p-2.5 bg-amber-500/10 rounded-xl text-amber-500 mb-2">
                <Flame className="w-5 h-5 fill-current text-amber-400" />
              </div>
              <h4 className="font-black text-2xl text-foreground block truncate">
                {(user.fame_score || 0) > 0 ? `+${user.fame_score}` : (user.fame_score || 0)}
              </h4>
              <p className="text-[10px] font-black uppercase tracking-wider text-muted-foreground mt-1 truncate">Trader Fame</p>
            </div>

            <div className="text-center group/stat p-3 bg-secondary/30 rounded-2xl border border-white/5">
              <div className="inline-flex p-2.5 bg-emerald-500/10 rounded-xl text-emerald-500 mb-2">
                <ThumbsUp className="w-5 h-5 text-emerald-400" />
              </div>
              <h4 className="font-black text-2xl text-emerald-400 block truncate">{user.likes?.length || 0} Likes</h4>
              <p className="text-[10px] font-black uppercase tracking-wider text-muted-foreground mt-1 truncate">Positive Ratings</p>
            </div>

            <div className="text-center group/stat p-3 bg-secondary/30 rounded-2xl border border-white/5">
              <div className="inline-flex p-2.5 bg-blue-500/10 rounded-xl text-blue-500 mb-2">
                <Award className="w-5 h-5" />
              </div>
              <h4 className="font-black text-xl text-foreground block uppercase truncate">
                {user.createdAt ? new Date(user.createdAt).getFullYear() : '2026'}
              </h4>
              <p className="text-[10px] font-black uppercase tracking-wider text-muted-foreground mt-1 truncate">{t('memberSince', 'Member Since')}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Inventory Section */}
      <div className="space-y-8">
        <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
            <div>
                <h2 className="text-4xl font-black bg-gradient-to-r from-primary to-purple-600 bg-clip-text text-transparent uppercase tracking-tighter">
                    {t('myItems', 'My Items')}
                </h2>
                <p className="text-muted-foreground font-bold mt-1 text-center sm:text-left text-xs uppercase tracking-widest opacity-60">{t('manageYourInventory', 'Manage your trade inventory')}</p>
            </div>
            <Link to="/create" className="w-full sm:w-auto bg-primary text-primary-content px-8 py-4 rounded-2xl font-black uppercase tracking-widest text-xs flex items-center justify-center gap-2 hover:shadow-2xl hover:shadow-primary/20 transition-all active:scale-95 shadow-xl">
                <Plus size={18} /> {t('listItem', 'List Item')}
            </Link>
        </div>

        {isLoadingItems ? (
            <div className="flex justify-center py-20"><Loader2 className="w-12 h-12 animate-spin text-primary" /></div>
        ) : myItems.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {myItems.map(item => (
                    <ItemCard 
                        key={item._id} 
                        item={item} 
                        showActions={true} 
                        onEdit={handleEditItem} 
                        onDelete={(id) => {
                            if(window.confirm('Delete this item?')) deleteMutation.mutate(id);
                        }}
                    />
                ))}
            </div>
        ) : (
            <div className="text-center bg-card rounded-[3rem] border border-dashed border-white/10 p-20">
                <div className="inline-flex p-6 bg-muted/30 rounded-full text-muted-foreground mb-6">
                    <Plus size={40} className="opacity-50" />
                </div>
                <h3 className="text-2xl font-black text-foreground mb-2">{t('noItemsYet', "No items yet")}</h3>
                <p className="text-muted-foreground max-w-sm mx-auto mb-8 font-bold">{t('startByListing', "Start by listing something you'd like to trade!")}</p>
                <Link to="/create" className="text-primary font-black uppercase tracking-widest text-sm hover:underline">{t('listItemNow', "List Item Now")}</Link>
            </div>
        )}
      </div>

      <EditProfileModal 
        isOpen={isEditModalOpen} 
        onClose={() => setIsEditModalOpen(false)} 
        user={user} 
      />
    </div>
  );
}
